import { z } from 'zod';

/**
 * Everything a principal may be allowed to do. Routes declare the capabilities they need, and
 * roles are named sets of them (the mapping lives in `@avrahami/auth`).
 *
 * - `platform.*` belongs to Avrahami's platform staff only. No tenant role may ever hold one.
 * - `store.*` is exercised inside one tenant. No platform role holds one. The platform staff
 *   reach a store's own admin only through `platform.tenants.manage`: a session of its own that
 *   manages one store as its owner would, recorded in that store's audit log (adr/0031).
 */
export const CAPABILITIES = [
  'platform.tenants.read',
  'platform.tenants.create',
  'platform.tenants.suspend',
  'platform.tenants.manage',
  'platform.users.manage',
  'platform.catalog.read',
  'platform.catalog.ingest',
  'platform.ai.view',
  'platform.ai.manage',
  'platform.audit.read',
  'platform.support.manage',
  'platform.system.read',
  'store.settings.read',
  'store.settings.write',
  'store.staff.read',
  'store.staff.manage',
  'store.audit.read',
  'store.billing.manage',
  'store.products.read',
  'store.products.write',
  'store.sync.run',
  'store.orders.read',
  'store.orders.update',
  'store.customers.read',
  'store.customers.update',
  'store.customers.export',
  'store.promotions.read',
  'store.promotions.write',
  'store.support.use',
] as const;

export const capabilitySchema = z.enum(CAPABILITIES);
export type Capability = z.infer<typeof capabilitySchema>;

export type PlatformCapability = Extract<Capability, `platform.${string}`>;
export type StoreCapability = Extract<Capability, `store.${string}`>;

export function isPlatformCapability(capability: Capability): capability is PlatformCapability {
  return capability.startsWith('platform.');
}

export function isStoreCapability(capability: Capability): capability is StoreCapability {
  return capability.startsWith('store.');
}
